import { Button } from "@/components/ui/button";
import { Plus, Download } from "lucide-react";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { UploadDocumentDialog } from "./UploadDocumentDialog";
import { EditDocumentDialog } from "./EditDocumentDialog";
import { useDocumentActions } from "../../hooks/useDocumentActions";
import { useAuth } from "@/features/auth/context/AuthContext";
import { useTranslation } from "react-i18next";
import { useFileUploader } from "@/components/features/FileUploader";
import { AssignRequiredDocumentsDialog } from "./AssignRequiredDocumentsDialog";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { ConfirmationDialog } from "@/components/common/ConfirmationDialog";
import { RequiredDocumentsSection } from "./RequiredDocumentsSection";
import { UploadedDocumentsSection } from "./UploadedDocumentsSection";

interface DocumentsSectionProps {
  isTalentProfile: boolean;
  application_id?: string;
  talent_id: string;
  canUploadDocuments: boolean;
  canAssignRequiredDocuments?: boolean;
  talentProfileData?: any;
  setTalentProfileData?: any;
}

export function DocumentsSection({
  application_id,
  talent_id,
  isTalentProfile,
  canUploadDocuments,
  canAssignRequiredDocuments,
  talentProfileData,
  setTalentProfileData
}: DocumentsSectionProps) {
  const [documents, setDocuments] = useState<any[]>([]);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    type: "list" | "document";
    id: number;
  } | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [updatedDocument, setUpdatedDocument] = useState<any>({});
  const [newDocument, setNewDocument] = useState<any>({
    documentTypeSearch: "",
    showDocumentTypeDropdown: false,
    documentTypeId: null,
    documentTypeName: null
  });
  const { profile: authProfile } = useAuth();

  const canEditFile = (document: any) => {
    if (authProfile?.isSuperUser) return true;
    if (document.talent_id === authProfile?.id) return true;
    if (application_id === document.application_id) return true;
    return false;
  };

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { handleViewFile, getTalentDocuments, getApplicationDocuments } =
    useFileUploader();
  const { t } = useTranslation();
  const { toast } = useToast();

  const refreshDocuments = useCallback(async () => {
    let newDocuments: any[] = [];
    try {
      newDocuments = [...(await getTalentDocuments(talent_id))].map(
        (document: any) => ({
          ...document,
          type: "talent"
        })
      );
      if (application_id) {
        newDocuments = [
          ...newDocuments,
          ...(await getApplicationDocuments(application_id)).map(
            (document: any) => ({
              ...document,
              type: "application"
            })
          )
        ];
      }
      setDocuments(newDocuments);
    } catch (error) {
      console.error("Error refreshing documents:", error);
    }
  }, [talent_id, application_id, getTalentDocuments, getApplicationDocuments]);

  const {
    saving,
    handleDocumentUpload,
    handleDocumentUpdate,
    handleDeleteDocument
  } = useDocumentActions({
    application_id,
    talent_id,
    isTalentProfile,
    onSuccess: async () => {
      await refreshDocuments();
      setIsUploadDialogOpen(false);
      setIsEditDialogOpen(false);
      setSelectedFile(null);
      setSelectedDocument(null);
      setUpdatedDocument({});
      setNewDocument({
        documentTypeSearch: "",
        showDocumentTypeDropdown: false,
        documentTypeId: null,
        documentTypeName: null
      });
    }
  });

  const prevIds = useRef<{ app: string | undefined; talent: string }>({
    app: undefined,
    talent: ""
  });

  useEffect(() => {
    async function fetchDocuments() {
      if (
        prevIds.current.app === application_id &&
        prevIds.current.talent === talent_id
      )
        return;

      prevIds.current = { app: application_id, talent: talent_id };
      await refreshDocuments();
    }

    fetchDocuments();
  }, [application_id, talent_id, refreshDocuments]);

  const handleRemoveRequiredItem = async () => {
    try {
      if (!itemToDelete || !talentProfileData) return;

      const currentReqDocTypes =
        talentProfileData.required_documents?.required_document_types || [];
      const currentReqLists =
        talentProfileData.required_documents?.required_documents_lists || [];

      const newRequiredDocuments = { ...talentProfileData.required_documents };

      if (itemToDelete.type === "document") {
        newRequiredDocuments.required_document_types =
          currentReqDocTypes.filter((id: number) => id !== itemToDelete.id);
      } else if (itemToDelete.type === "list") {
        newRequiredDocuments.required_documents_lists = currentReqLists.filter(
          (id: number) => id !== itemToDelete.id
        );
      }

      const { error } = await supabase
        .from("talent_profiles")
        .update({ required_documents: newRequiredDocuments })
        .eq("id", talent_id);

      if (error) throw error;

      if (setTalentProfileData) {
        setTalentProfileData({
          ...talentProfileData,
          required_documents: newRequiredDocuments
        });
      }

      toast({
        title: "Erfolg",
        description: "Anforderung wurde entfernt."
      });
    } catch (error) {
      console.error("Error removing required item:", error);
      toast({
        title: "Fehler",
        description: "Anforderung konnte nicht entfernt werden.",
        variant: "destructive"
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleEditClick = (docToEdit: any) => {
    const mappedDoc = {
      id: docToEdit.id,
      documentTypeId: docToEdit.document_type_id,
      documentTypeSearch: "",
      showDocumentTypeDropdown: false,
      documentStatus: docToEdit.document_status
    };
    setSelectedDocument(mappedDoc);
    setUpdatedDocument(mappedDoc);
    setIsEditDialogOpen(true);
  };

  return (
    <>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 md:mb-6 gap-4 md:gap-0">
        <h2 className="text-xl font-semibold">{t("common:documents.title")}</h2>
        <div className="flex gap-2 w-full md:w-auto">
          {canAssignRequiredDocuments && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 md:flex-none"
              onClick={() => setIsAssignDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Erforderliche Dokumente zuweisen
            </Button>
          )}
          {canUploadDocuments && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 md:flex-none"
              id="upload-document-button"
              onClick={() => {
                setNewDocument({
                  documentTypeSearch: "",
                  showDocumentTypeDropdown: false,
                  documentTypeId: null,
                  documentTypeName: null
                });
                setIsUploadDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("common:documents.upload_document")}
            </Button>
          )}
          <Button variant="outline" size="sm" className="flex-1 md:flex-none">
            <Download className="h-4 w-4 mr-2" />
            {t("common:documents.download_all")}
          </Button>
        </div>
      </div>

      <RequiredDocumentsSection
        talentProfileData={talentProfileData}
        documents={documents}
        canUploadDocuments={canUploadDocuments}
        onUpload={(docId, docName) => {
          setNewDocument({
            ...newDocument,
            documentTypeId: docId,
            documentTypeSearch: docName,
            documentTypeName: docName
          });
          setIsUploadDialogOpen(true);
        }}
        onView={(doc) =>
          handleViewFile(
            doc.application_id ? "applications" : "profiles",
            doc.file_path
          )
        }
        onEdit={handleEditClick}
        onDeleteUploaded={handleDeleteDocument}
        onRemoveRequirement={(id, type) => {
          setItemToDelete({ id, type });
          setIsDeleteDialogOpen(true);
        }}
        canEditFile={canEditFile}
      />

      <div className="mb-2">
        <h3 className="text-lg font-medium">
          Zusätzliche / Hochgeladene Dokumente
        </h3>
      </div>

      <div className="space-y-6">
        <UploadedDocumentsSection
          documents={documents}
          talentProfileData={talentProfileData}
          application_id={application_id}
          canEditFile={canEditFile}
          onView={handleViewFile}
          onEdit={handleEditClick}
          onDelete={handleDeleteDocument}
        />
      </div>

      <UploadDocumentDialog
        isOpen={isUploadDialogOpen}
        onOpenChange={setIsUploadDialogOpen}
        newDocument={newDocument}
        setNewDocument={setNewDocument}
        setSelectedFile={setSelectedFile}
        handleUpload={async (e: React.FormEvent) => {
          e.preventDefault();
          await handleDocumentUpload(selectedFile, newDocument.documentTypeId);
        }}
        saving={saving}
      />

      <EditDocumentDialog
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        selectedDocument={selectedDocument}
        updatedDocument={updatedDocument}
        setUpdatedDocument={setUpdatedDocument}
        handleUpdate={async (e: React.FormEvent) => {
          e.preventDefault();
          await handleDocumentUpdate(selectedDocument.id, {
            document_type_id:
              updatedDocument.documentTypeId || selectedDocument.documentTypeId,
            document_status:
              updatedDocument.documentStatus !== undefined
                ? updatedDocument.documentStatus
                : selectedDocument.documentStatus
          });
        }}
        saving={saving}
      />

      <AssignRequiredDocumentsDialog
        isOpen={isAssignDialogOpen}
        onOpenChange={setIsAssignDialogOpen}
        talentId={talent_id}
        currentRequiredDocuments={talentProfileData?.required_documents}
        onSuccess={(newRequired) => {
          if (setTalentProfileData && talentProfileData) {
            setTalentProfileData({
              ...talentProfileData,
              required_documents: newRequired
            });
          }
        }}
      />

      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title={
          itemToDelete?.type === "list"
            ? t(
                "common:documents.requirements.remove_list_title",
                "Liste entfernen"
              )
            : t(
                "common:documents.requirements.remove_doc_title",
                "Dokument entfernen"
              )
        }
        description={
          itemToDelete?.type === "list"
            ? t(
                "common:documents.requirements.remove_list_desc",
                "Möchten Sie diese Dokumentenliste wirklich aus den Anforderungen entfernen?"
              )
            : t(
                "common:documents.requirements.remove_doc_desc",
                "Möchten Sie dieses Dokument wirklich aus den Anforderungen entfernen?"
              )
        }
        onConfirm={handleRemoveRequiredItem}
        confirmText={t("common:actions.delete", "Löschen")}
        cancelText={t("common:actions.cancel", "Abbrechen")}
        variant="destructive"
      />
    </>
  );
}
